import { useState, useEffect } from 'react';
import { ChevronDown, Globe, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSupabaseClient } from '@em-cms/db';
import type { Site } from '@em-cms/core';

export function SiteSwitcher() {
  const { currentSite, setCurrentSite } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const supabase = getSupabaseClient();

  useEffect(() => {
    loadSites();
  }, []);

  async function loadSites() {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('cms_sites')
      .select('*')
      .eq('is_active', true)
      .order('name');
    
    if (error) {
      console.error('Error loading sites:', error);
    } else {
      setSites(data || []);
      // Auto-select first site if none selected
      if (!currentSite && data && data.length > 0) {
        setCurrentSite(data[0]);
      }
    }
    setIsLoading(false);
  }

  const handleSelect = (site: Site) => {
    setCurrentSite(site);
    setIsOpen(false);
  };

  if (isLoading) {
    return <div className="px-3 py-2 text-sm text-gray-400">در حال بارگذاری...</div>;
  }

  if (sites.length === 0) {
    return (
      <div className="px-3 py-2 text-sm text-gray-500">سایتی یافت نشد</div>
    );
  }

  // Single site — no dropdown needed
  if (sites.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 text-primary-700 w-full">
        <Globe size={18} />
        <span className="flex-1 text-right truncate font-medium">{sites[0].name}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors w-full"
      >
        <Globe size={18} />
        <span className="flex-1 text-right truncate">
          {currentSite?.name || 'انتخاب سایت'}
        </span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-auto">
            {sites.map((site) => (
              <button
                key={site.id}
                onClick={() => handleSelect(site)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-right hover:bg-gray-50 transition-colors ${
                  currentSite?.id === site.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                }`}
              >
                {currentSite?.id === site.id && <Check size={16} className="text-primary-600" />}
                <span className="flex-1 truncate">{site.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
